import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  fetchGalleryV2,
  generateTimePointsV2,
} from "../services/galleryServiceV2";
import { GalleryDataV2, PhotoV2, FolderV2 } from "../types/v2";
import { Photo, TimePoint } from "../types";
import GalleryComponent from "./Gallery";
import EventInfo from "./EventInfo";
import "../styles/EventGallery.css";
import "../styles/Modal.css";
import "../styles/LanguageSelector.css";
import { Language, eventPageTranslations } from "../translations";
import "../styles/EventList.css";
import "../styles/EventTile.css";

// Convert PhotoV2 to Photo for compatibility with existing Gallery component
const convertToLegacyPhoto = (photo: PhotoV2): Photo => ({
  id: photo.id,
  eventId: photo.galleryId,
  url: photo.url,
  thumbnailUrl: photo.thumbnailUrl,
  takenAt: photo.takenAt,
  width: photo.width,
  height: photo.height,
});

// Get total file count from a folder (including all sub-folders recursively)
const getTotalFileCount = (folder: FolderV2): number => {
  let count = folder.files.length;
  for (const subFolder of folder.subFolders) {
    count += getTotalFileCount(subFolder);
  }
  return count;
};

// Get file IDs from a specific folder (for filtering pre-sorted photos)
const getFileIdsFromFolder = (
  folders: FolderV2[],
  folderId: string,
): Set<string> => {
  const findFolder = (folders: FolderV2[]): FolderV2 | null => {
    for (const folder of folders) {
      if (folder.id === folderId) return folder;
      const found = findFolder(folder.subFolders);
      if (found) return found;
    }
    return null;
  };

  const folder = findFolder(folders);
  if (!folder) return new Set();

  return new Set(folder.files.map((file) => file.id));
};

const GalleryPageV2: React.FC = () => {
  const { shareId, folderId } = useParams<{
    shareId: string;
    folderId?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gallery, setGallery] = useState<GalleryDataV2 | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isIntroHidden, setIsIntroHidden] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [language, setLanguage] = useState<Language>("pl");
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);

  const translations = eventPageTranslations[language];

  // Handle language change and update query param
  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    const params = new URLSearchParams(searchParams);
    params.set("lang", newLanguage);
    setSearchParams(params, { replace: true });
  };

  // Handle photo selection and update query param
  const handlePhotoSelect = (photoId: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (photoId) {
      params.set("photo", photoId);
    } else {
      params.delete("photo");
    }
    setSearchParams(params, { replace: true });
  };

  // Get selected photo ID from query param
  const selectedPhotoId = searchParams.get("photo");

  // Load gallery data
  useEffect(() => {
    if (!shareId) return;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Check for language in query param
        const langParam = searchParams.get("lang") as Language | null;
        const requestLanguage =
          langParam && (langParam === "pl" || langParam === "en")
            ? langParam
            : "pl";

        const galleryData = await fetchGalleryV2(shareId, requestLanguage);

        if (!galleryData) {
          setError("Gallery not found or unavailable");
          return;
        }

        setGallery(galleryData);
        setLanguage(requestLanguage);

        // Update URL with language if not present
        if (!langParam) {
          const params = new URLSearchParams(searchParams);
          params.set("lang", requestLanguage);
          setSearchParams(params, { replace: true });
        }

        // Check if we should show language selector (if i18n has multiple languages)
        setShowLanguageSelector(galleryData.isMultilang);

        // Hide intro if no description
        if (!galleryData.description) {
          setIsIntroHidden(true);
        }
      } catch (err) {
        console.error("Error loading gallery data:", err);
        setError("Failed to load gallery");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId]);

  // Get photos for current view (all or specific folder)
  // Use pre-sorted gallery.photos to maintain chronological order
  const photos = useMemo(() => {
    if (!gallery) return [];
    if (!folderId) {
      // Return all photos (already sorted by takenAt from service)
      return gallery.photos;
    }
    // Filter to only photos in the specific folder, maintaining sort order
    const folderFileIds = getFileIdsFromFolder(gallery.folders, folderId);
    return gallery.photos.filter((photo) => folderFileIds.has(photo.id));
  }, [gallery, folderId]);

  // Convert to legacy format for Gallery component
  const legacyPhotos = useMemo(
    () => photos.map(convertToLegacyPhoto),
    [photos],
  );

  // Generate time points for timeline navigation from photos
  const timePoints: TimePoint[] = useMemo(() => {
    const v2TimePoints = generateTimePointsV2(photos);
    // Convert TimePointV2 to TimePoint (they have the same structure)
    return v2TimePoints.map((tp) => ({
      time: tp.time,
      fullDateTime: tp.fullDateTime,
      isoDateTime: tp.isoDateTime,
      position: tp.position,
    }));
  }, [photos]);

  if (isLoading) {
    return <div className="loading">{translations.loadingText}</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!gallery) {
    return <div className="error">Gallery not found</div>;
  }

  // Find current folder if viewing a specific folder
  const getCurrentFolder = (): FolderV2 | null => {
    if (!folderId) return null;
    const findFolder = (folders: FolderV2[]): FolderV2 | null => {
      for (const folder of folders) {
        if (folder.id === folderId) return folder;
        const found = findFolder(folder.subFolders);
        if (found) return found;
      }
      return null;
    };
    return findFolder(gallery.folders);
  };

  const currentFolder = getCurrentFolder();
  const currentFolderName = currentFolder
    ? currentFolder.i18n?.[language]?.name || currentFolder.name
    : null;

  return (
    <div className="event-gallery-page">
      <header className="gallery-header">
        {folderId && (
          <Link
            to={`/galleries/${shareId}?lang=${language}`}
            className="back-button"
          >
            {translations.backButtonText}
          </Link>
        )}
        <h1>{currentFolderName || gallery.title}</h1>
        <button className="show-info" onClick={() => setShowInfoModal(true)}>
          {translations.infoButtonText}
        </button>
        {gallery.date && (
          <p className="event-date-display">
            {new Date(gallery.date).toLocaleDateString(undefined, {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              timeZone: "Europe/Warsaw",
            })}
          </p>
        )}
        {showLanguageSelector && (
          <div className="language-selector">
            <button
              className={language === "pl" ? "active" : ""}
              onClick={() => handleLanguageChange("pl")}
            >
              PL
            </button>
            <button
              className={language === "en" ? "active" : ""}
              onClick={() => handleLanguageChange("en")}
            >
              EN
            </button>
          </div>
        )}
        {!isIntroHidden && gallery.description && (
          <section className="intro">
            <p>{gallery.description}</p>
            <button
              className="close-intro"
              onClick={() => setIsIntroHidden(true)}
            >
              {translations.closeIntroButton}
            </button>
          </section>
        )}
      </header>

      {/* Show folder tiles */}
      {(() => {
        // Determine which folders to show
        const foldersToShow =
          folderId && currentFolder
            ? currentFolder.subFolders // Show subfolders if in a folder
            : gallery.folders; // Show root folders otherwise

        // Only show if there are multiple folders OR if we have subfolders
        if (foldersToShow.length === 0) return null;
        if (!folderId && foldersToShow.length <= 1) return null;

        return (
          <div className="event-list-container">
            <div className="event-grid">
              {foldersToShow.map((folder) => (
                <Link
                  key={folder.id}
                  to={`/galleries/${shareId}/folder/${folder.id}?lang=${language}`}
                  className="event-card"
                >
                  <div className="event-image-container">
                    {folder.coverFileUrl && (
                      <img
                        src={folder.coverFileUrl}
                        alt={folder.i18n?.[language]?.name || folder.name}
                        className="event-cover-image"
                      />
                    )}
                  </div>
                  <div className="event-info">
                    <h3 className="event-name">{folder.i18n?.[language]?.name || folder.name}</h3>
                    <p className="event-photo-count">{getTotalFileCount(folder)} files</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Show photos only if no subfolders in current view */}
      {(() => {
        const hasSubFolders =
          folderId && currentFolder
            ? currentFolder.subFolders.length > 0
            : gallery.folders.length > 1;

        const shouldShowPhotos = !hasSubFolders && legacyPhotos.length > 0;

        if (!shouldShowPhotos) return null;

        return (
          <GalleryComponent
            photos={legacyPhotos}
            timePoints={timePoints}
            isLoading={isLoading}
            showFullDate={false}
            onIsFirstChange={() => {}}
            showDownloadButton={false}
            language={language}
            selectedPhotoId={selectedPhotoId}
            onPhotoSelect={handlePhotoSelect}
          />
        );
      })()}

      {showInfoModal && (
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowInfoModal(false)}
            >
              x
            </button>
            <EventInfo
              language={language}
              onLanguageChange={handleLanguageChange}
              showLanguageSelector={showLanguageSelector}
              gallery={{
                id: gallery.id,
                i18n: {},
                defaultLanguage: language,
                subGalleries: [],
                photos: legacyPhotos,
                date: gallery.date,
                availableUntilDate: gallery.expiresAt,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPageV2;
